/** Coordination leases for shared provider credentials / egress groups (spec section 6). */
import { z } from 'zod';
import { DeviceIdSchema, LeaseIdSchema, ProviderSchema } from '../ids.js';
import { IsoDateTimeSchema, defineRoute } from './common.js';
import { LeasePurposeSchema, ProviderLeaseSchema } from './entities.js';

export const LEASE_MAX_TTL_SECONDS = 15 * 60;
export const LEASE_DEFAULT_TTL_SECONDS = 5 * 60;

export const LeaseAcquireRequestSchema = z
  .object({
    provider: ProviderSchema,
    /** Opaque quota group: connection fingerprint (Vanta) or egress group id (Drata). */
    quotaGroup: z.string().min(8).max(128),
    deviceId: DeviceIdSchema,
    purpose: LeasePurposeSchema,
    ttlSeconds: z.number().int().min(10).max(LEASE_MAX_TTL_SECONDS).default(LEASE_DEFAULT_TTL_SECONDS),
    /** Quota metadata only (requests per minute the holder intends to use). */
    requestedRatePerMinute: z.number().int().positive().max(1000).optional(),
  })
  .strict();
export type LeaseAcquireRequest = z.infer<typeof LeaseAcquireRequestSchema>;

export const LeaseAcquireResponseSchema = z.object({
  lease: ProviderLeaseSchema,
  /** Rate ceiling the coordinator allows the holder for this group. */
  allowedRatePerMinute: z.number().int().positive(),
});
export type LeaseAcquireResponse = z.infer<typeof LeaseAcquireResponseSchema>;

/** Body of a 409 lease_held response. */
export const LeaseHeldDetailsSchema = z.object({
  heldUntil: IsoDateTimeSchema,
  heldByDeviceId: DeviceIdSchema,
  heldBySelf: z.boolean(),
  purpose: LeasePurposeSchema,
});
export type LeaseHeldDetails = z.infer<typeof LeaseHeldDetailsSchema>;

export const LeaseRenewRequestSchema = z
  .object({
    fencingToken: z.number().int().positive(),
    ttlSeconds: z.number().int().min(10).max(LEASE_MAX_TTL_SECONDS).default(LEASE_DEFAULT_TTL_SECONDS),
  })
  .strict();
export const LeaseReleaseRequestSchema = z.object({ fencingToken: z.number().int().positive() }).strict();
export const LeaseResponseSchema = z.object({ lease: ProviderLeaseSchema });
export const LeaseParamsSchema = z.object({ leaseId: LeaseIdSchema });

export const providerLeaseRoutes = {
  'leases.acquire': defineRoute({
    id: 'leases.acquire',
    method: 'POST',
    path: '/v1/provider-leases',
    auth: 'desktop',
    capability: ['grc.sync', 'account.connect', 'credential.debug'],
    requestSchema: LeaseAcquireRequestSchema,
    responseSchema: LeaseAcquireResponseSchema,
    description: 'Acquire the quota-group lease with a fencing token; 409 lease_held when another device holds it.',
  }),
  'leases.renew': defineRoute({
    id: 'leases.renew',
    method: 'POST',
    path: '/v1/provider-leases/:leaseId/renew',
    auth: 'desktop',
    paramsSchema: LeaseParamsSchema,
    requestSchema: LeaseRenewRequestSchema,
    responseSchema: LeaseResponseSchema,
    description: 'Extend a held lease; fencing token must match.',
  }),
  'leases.release': defineRoute({
    id: 'leases.release',
    method: 'POST',
    path: '/v1/provider-leases/:leaseId/release',
    auth: 'desktop',
    paramsSchema: LeaseParamsSchema,
    requestSchema: LeaseReleaseRequestSchema,
    responseSchema: LeaseResponseSchema,
    description: 'Release a held lease.',
  }),
} as const;
